import './AccountSettings.scss';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../auth/AuthContext';
import { fetchProfile, updateProfile } from '../../services/api/profileService';
import GuestLoginPanel from '../GuestAuthDropdown/GuestLoginPanel';

export default function AccountSettings() {
  const { isAuthenticated, token, setSession, customer } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const loadProfile = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await fetchProfile(token);
      setName(data.name || '');
      setEmail(data.email || '');
      setPhone(data.phone || '');
    } catch (e) {
      toast.error(e?.message || 'Could not load profile');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) loadProfile();
  }, [isAuthenticated, loadProfile]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const trimmedName = name.trim();
      const trimmedPhone = phone.trim();

      setSaving(true);
      try {
        const updated = await updateProfile(
          { name: trimmedName || null, phone: trimmedPhone || null },
          token,
        );
        setName(updated.name || '');
        setEmail(updated.email || '');
        setPhone(updated.phone || '');

        setSession(token, { ...customer, name: updated.name, phone: updated.phone });

        toast.success('Profile updated');
      } catch (e) {
        toast.error(e?.message || 'Could not save changes');
      } finally {
        setSaving(false);
      }
    },
    [name, phone, token, customer, setSession],
  );

  if (!isAuthenticated) {
    return (
      <div className="as-page">
        <div className="as-page__inner">
          <div className="as-card as-auth-gate">
            <p className="as-auth-gate__title">Sign in to view your account</p>
            <p className="as-auth-gate__sub">
              Enter your email and we'll send a one-time code. No password needed.
            </p>
            <GuestLoginPanel
              heading="SIGN IN"
              onAuthenticated={() => toast.success('Signed in!')}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="as-page">
      <div className="as-page__inner">
        <nav className="as-breadcrumb">
          <Link to="/" className="as-breadcrumb__link">My profile</Link>
          <span className="as-breadcrumb__sep">/</span>
          <span className="as-breadcrumb__current">Account details</span>
        </nav>

        <div className="as-card">
          <h2 className="as-section-title">Contact</h2>

          {loading ? (
            <div className="as-skeleton">
              <div className="as-skeleton__bar" />
              <div className="as-skeleton__bar" />
              <div className="as-skeleton__bar as-skeleton__bar--short" />
            </div>
          ) : (
            <form className="as-form" onSubmit={handleSubmit}>
              <div className="as-field">
                <label className="as-label" htmlFor="as-name">Full name</label>
                <input
                  id="as-name"
                  className="as-input"
                  type="text"
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="as-field">
                <label className="as-label" htmlFor="as-email">
                  Email<span className="as-label__required">*</span>
                </label>
                <input
                  id="as-email"
                  className="as-input"
                  type="email"
                  value={email}
                  readOnly
                />
              </div>

              <div className="as-field">
                <label className="as-label" htmlFor="as-phone">Mobile number</label>
                <div className="as-phone-row">
                  <div className="as-phone-prefix">
                    <span className="as-phone-prefix__flag">🇺🇸</span>
                    <span>+1</span>
                  </div>
                  <input
                    id="as-phone"
                    className="as-input as-phone-input"
                    type="tel"
                    placeholder=""
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
              </div>

              <button type="submit" className="as-btn" disabled={saving}>
                {saving ? 'Saving...' : 'Save updates'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
